import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { Clock, GlassWater, ArrowLeft } from "lucide-react";
import { getCocktail, COCKTAILS } from "../data/cocktails";
import PogLogo from "../components/PogLogo";
import ShareBar from "../components/ShareBar";

const CANONICAL = "https://pogspirits.com";

const CocktailRecipe = () => {
  const { slug } = useParams();
  const cocktail = getCocktail(slug);

  if (!cocktail) return <Navigate to="/cocktails" replace />;

  const url = `${CANONICAL}/cocktails/${cocktail.slug}`;
  const related = COCKTAILS.filter((c) => c.slug !== cocktail.slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": url,
    name: `${cocktail.name} — POG Spirits Cocktail Recipe`,
    description: cocktail.description,
    image: `${CANONICAL}${cocktail.image}`,
    totalTime: `PT${cocktail.timeMinutes}M`,
    yield: cocktail.slug === "peles-punch-bowl" ? "6 servings" : "1 cocktail",
    recipeCuisine: "Hawaiian",
    tool: [{ "@type": "HowToTool", name: cocktail.glass }],
    supply: cocktail.ingredients.map((i) => ({ "@type": "HowToSupply", name: i })),
    step: cocktail.steps.map((s) => ({ "@type": "HowToStep", text: s })),
  };

  return (
    <div className="min-h-screen bg-[#14100D] text-[#EFE7D6]" data-testid="recipe-page">
      <Helmet>
        <title>{`${cocktail.name} — POG Spirits Cocktail Recipe`}</title>
        <meta name="description" content={cocktail.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${cocktail.name} — POG Spirits Cocktail Recipe`} />
        <meta property="og:description" content={cocktail.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${CANONICAL}${cocktail.image}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#14100D]/90 backdrop-blur-xl border-b border-[#EFE7D6]/10">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="recipe-logo-home">
            <PogLogo size={28} />
            <span className="font-body italic text-[#EFE7D6] tracking-wide">Spirits</span>
          </Link>
          <Link
            to="/cocktails"
            className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline flex items-center gap-2"
            data-testid="recipe-back-gallery"
          >
            <ArrowLeft size={14} /> All cocktails
          </Link>
        </div>
      </header>

      <main className="pt-40 pb-32 px-8 md:px-12 max-w-[1200px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <div className="overline text-[10px] mb-6 text-[#EFE7D6]/50" data-testid="recipe-meta">
            {cocktail.base} · {cocktail.difficulty} · {cocktail.occasion}
          </div>
          <h1 className="display-tight text-5xl sm:text-6xl md:text-7xl mb-6" data-testid="recipe-title">
            {cocktail.name}
          </h1>
          <p className="font-body text-xl text-[#DCCFB4]/80 max-w-2xl leading-[1.6] mb-10">
            {cocktail.tagline}
          </p>

          <div className="flex flex-wrap items-center gap-8 mb-14 text-sm text-[#EFE7D6]/70">
            <span className="flex items-center gap-2" data-testid="recipe-time">
              <Clock size={16} className="text-[#F26B1F]" /> {cocktail.timeMinutes} min
            </span>
            <span className="flex items-center gap-2" data-testid="recipe-glass">
              <GlassWater size={16} className="text-[#F26B1F]" /> {cocktail.glass}
            </span>
          </div>

          <div className="mb-14">
            <ShareBar url={url} title={`${cocktail.name} — POG Spirits cocktail recipe`} testid="recipe-share" />
          </div>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-14 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-2"
          >
            <div
              className="relative aspect-[3/4] overflow-hidden mb-10"
              style={{ background: "radial-gradient(ellipse at 50% 45%, #2a1f18 0%, #0f0a07 80%)" }}
            >
              <div className="absolute inset-0 opacity-20 sunburst-ghost pointer-events-none" />
              <img
                src={cocktail.image}
                alt={`${cocktail.name} — POG Spirits cocktail recipe`}
                className="absolute inset-0 w-full h-full object-contain p-12"
              />
            </div>
            <div className="overline mb-4">Ingredients</div>
            <ul className="space-y-3" data-testid="recipe-ingredients">
              {cocktail.ingredients.map((ing) => (
                <li key={ing} className="font-body text-lg text-[#EFE7D6]/90 leading-relaxed">
                  — {ing}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="md:col-span-3"
          >
            <div className="overline mb-6">Method</div>
            <ol className="space-y-8" data-testid="recipe-steps">
              {cocktail.steps.map((step, i) => (
                <li key={i} className="flex gap-6">
                  <span className="font-display text-3xl text-[#F26B1F]/60 shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-body text-lg text-[#DCCFB4]/90 leading-[1.75] font-light">{step}</p>
                </li>
              ))}
            </ol>

            <div className="mt-14 border border-[#EFE7D6]/10 p-8">
              <div className="overline mb-3">Garnish</div>
              <p className="font-body text-[#EFE7D6]/85 text-lg font-light" data-testid="recipe-garnish">
                {cocktail.garnish}
              </p>
            </div>
          </motion.div>
        </div>

        <div className="mt-28 border-t border-[#EFE7D6]/10 pt-16">
          <div className="overline mb-10">Keep mixing</div>
          <div className="grid sm:grid-cols-2 gap-10">
            {related.map((c) => (
              <Link
                key={c.slug}
                to={`/cocktails/${c.slug}`}
                className="group border border-[#EFE7D6]/10 p-8 hover:border-[#F26B1F]/50 transition-colors duration-300"
                data-testid={`recipe-related-${c.slug}`}
              >
                <div className="overline text-[10px] mb-3 text-[#EFE7D6]/50">
                  {c.base} · {c.difficulty}
                </div>
                <h3 className="font-display text-3xl group-hover:text-[#F26B1F] transition-colors duration-300">
                  {c.name}
                </h3>
                <p className="font-body text-[#DCCFB4]/70 text-sm mt-3">{c.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-[#EFE7D6]/10 px-8 py-10 text-center">
        <p className="text-xs text-[#EFE7D6]/40 tracking-wider">
          POG Spirits · Please enjoy responsibly · 21+
        </p>
      </footer>
    </div>
  );
};

export default CocktailRecipe;
